import { Button, TextField } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useEffect, useState, lazy, forwardRef } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import Headline from '../../../components/headline/headline';
import Paragraph from '../../../components/paragraph/paragraph';
import { useStyles } from './sectionDealerLocator.jss';

const Icon = lazy(() => import('../../../components/icon/icon'));

const SectionDealerLocator = forwardRef(({
  translations: {
    headline,
    headlinePrimary,
    labelSearchByPlace,
    labelGeolocalization,
    placeholderGeolocalization,
    loadingText
  },
  onPlaceSelected,
  place
}, ref) => {
  const classes = useStyles();
  const [value, setValue] = useState(null);
  const [options, setOptions] = useState([]);
  const { palette } = useTheme();

  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } =
    usePlacesService({
      apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
      options: {
        componentRestrictions: { country: 'pl' }
      }
    });

  useEffect(() => {
    if (placePredictions.length) setOptions(placePredictions);
  }, [placePredictions]);

  useEffect(() => {
    if (!place) setValue(null);
  }, [place]);

  const selectPlace = (place) => {
    if (place) {
      const placeId = place?.place_id;
      placesService?.getDetails({ placeId }, (placeDetails) =>
        onPlaceSelected({
          latitude: placeDetails?.geometry?.location.lat(),
          longitude: placeDetails?.geometry?.location.lng()
        })
      );
    } else onPlaceSelected(null);
  };

  const opts = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0
  };

  function error(err) {
    console.warn(`ERROR(${err.code}): ${err.message}`);
  }

  const getPosition = (data) => {
    setValue(placeholderGeolocalization);
    onPlaceSelected({
      latitude: data?.coords?.latitude,
      longitude: data?.coords?.longitude
    });
  };

  return (
    <>
      <Headline variant="h2" headline={headline} headlinePrimary={headlinePrimary} />
      <div className={classes.container}>
        <Button
          color="primary"
          variant="outlined"
          classes={{
            root: classes.locationRoot,
            label: classes.locationLabel
          }}
          onClick={() => navigator.geolocation.getCurrentPosition(getPosition, error, opts)}
        >
          {labelGeolocalization}
          <Icon id="Geoloc" size="16" color={palette.common.iconGeolocalization} marginLeft={2} />
        </Button>
        <Autocomplete
          options={options}
          filterOptions={(item) => item}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
          renderOption={(option) => <Paragraph variant="body1">{option.description}</Paragraph>}
          renderInput={(params) => (
            <TextField
              {...params}
              ref={ref}
              label={
                <Paragraph className={classes.labelSearchByPlace} variant="body1" color="primary">
                  {labelSearchByPlace}
                </Paragraph>
              }
              variant="outlined"
              InputLabelProps={{
                classes: {
                  formControl: classes.autocompleteInputLabel
                }
              }}
              InputProps={{
                ...params.InputProps,
                classes: {
                  root: classes.root,
                  input: classes.input,
                  notchedOutline: classes.notchedOutline
                },
                endAdornment: (
                  <>
                    {!value ? (
                      <div className={classes.adornedEnd}>
                        <Icon id="Search" size="15" color={palette.common.iconSearch} />
                      </div>
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          classes={{
            root: classes.autocompleteRoot,
            inputRoot: classes.autocompleteInput
          }}
          value={value}
          noOptionsText={labelSearchByPlace}
          loadingText={loadingText}
          loading={isPlacePredictionsLoading}
          onInputChange={(_, value) => {
            setValue(value);
            getPlacePredictions({ input: value });
          }}
          onChange={(_, value) => {
            setValue(value);
            selectPlace(value);
          }}
          getOptionSelected={(option, value) => option.iso === value.iso}
          includeInputInList
          autoComplete
          freeSolo
        ></Autocomplete>
      </div>
    </>
  );
});

export default SectionDealerLocator;
